import * as amplitude from '@amplitude/unified';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CookieConsent,
} from 'components';
import { AppSidebar } from 'components/app-sidebar';
import { SidebarInset, SidebarProvider } from 'components/ui/sidebar';
import { Toaster } from 'components/ui/sonner';
import { CurrentUser, useCurrentUser } from 'hooks/use-current-user';
import { ErrorBoundary } from 'layout';
import { SIGN_IN_PATH, signInPath } from 'lib/login-method';
import get from 'lodash/get';
import isString from 'lodash/isString';
import startsWith from 'lodash/startsWith';
import { ErrorPage, LoadingPage } from 'pages';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { security } from 'shared';

interface PageLayoutProps {
  children?: React.ReactElement;
}

const PageLayout = ({ children }: PageLayoutProps) => {
  const auth0 = useAuth0();
  const history = useHistory();
  const { location } = history;

  // OilyticsClient.create() resolves tokens through the shared security
  // module, so the Auth0 token getter must be registered before the user
  // query is allowed to run.
  const [tokenReady, setTokenReady] = useState(false);
  useEffect(() => {
    if (!auth0.isLoading && auth0.isAuthenticated) {
      security.setAccessTokenSilently(auth0.getAccessTokenSilently);
      setTokenReady(true);
    }
  }, [auth0.isLoading, auth0.isAuthenticated, auth0.getAccessTokenSilently]);

  const userQuery = useCurrentUser({
    enabled: !auth0.isLoading && auth0.isAuthenticated && tokenReady,
  });
  const user: CurrentUser = userQuery.data ?? {};
  const userError = userQuery.error
    ? get(userQuery.error, 'error') || userQuery.error
    : null;

  // The sign-in page owns the code-vs-password choice (and the `login`
  // analytics event, tagged with the chosen method).
  const login = () => {
    history.push(signInPath(location));
  };

  const logout = () => {
    amplitude.track('logout');
    auth0.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // When the users refresh token has expired, auth0 returns an error with error =  'invalid_grant' and
  // error_description = 'Unknown or invalid refresh token.' Since we only propogate the error, assume that
  // any invalid grant is because of an expired refresh token and prompt the user to login again
  const shouldRedirect = !!(!auth0.isLoading && auth0.error && auth0.error.error_description === 'Unknown or invalid refresh token.') || userError === 'invalid_grant';

  useEffect(() => {
    // Logout user and clear tokens if refresh token is invalid, then hard-
    // navigate to the sign-in page. A full page load (rather than a router
    // push) is deliberate: the local-only logout leaves auth0-react's
    // `error` and the cached user query in place, and a reload is the
    // simplest way to start the sign-in page from a clean slate. This path
    // previously left the SPA for Universal Login anyway.
    if (shouldRedirect) {
      auth0.logout({
        logoutParams: {
          localOnly: true,
        },
      });
      // eslint-disable-next-line lodash/prefer-lodash-method -- Location.replace, not String.replace
      window.location.replace(signInPath(location));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirect]);

  let error = userError || get(auth0, 'error');
  // Convert string error to object for ErrorPage
  if (isString(error)) {
    console.warn('ErrorPage received a string error. Converting to object.', error);
    error = { name: 'Error', message: error };
  }
  const isLoading = auth0.isLoading || (!error && !user.email && auth0.isAuthenticated);

  // The sign-in page is for signed-out visitors, so it renders without
  // waiting on (or erroring from) the current-user query; it handles the
  // Auth0 initialising state itself.
  const showChildren = location.pathname === SIGN_IN_PATH || (!isLoading && !error && !shouldRedirect);

  let loadingMessage = 'Loading user';
  if (auth0.isLoading) {
    loadingMessage = 'Initializing';
  } else if (shouldRedirect) {
    loadingMessage = 'Redirecting you to the login page';
  }

  return (
    <React.Fragment>
      <SidebarProvider defaultOpen={window.innerWidth >= 1024}>
        <div className="flex h-screen w-screen">
          <AppSidebar
            showNavMain={auth0.isAuthenticated && !!user.email}
            isAuthenticated={auth0.isAuthenticated}
            user={auth0.isAuthenticated && user.email ? { name: user.name || user.email, email: user.email } : null}
            onLogin={login}
            onLogout={logout}
          />
          <SidebarInset className="flex-1 min-w-0">
            {/* Chat and geopolitics are responsive — they manage their own width
                constraints and shouldn't be forced to 1024px. Other pages still
                need the min-width to keep dashboards/watchlists laid out. */}
            <div className={`w-full h-full${location.pathname === '/chat' || startsWith(location.pathname, '/chat/') || location.pathname === '/geopolitics' ? '' : ' min-w-[1024px] p-4'}`}>
              {(!showChildren && isLoading && (!error || shouldRedirect)) && <LoadingPage message={loadingMessage} />}
              {(!showChildren && !!error && !shouldRedirect) && <ErrorPage error={error} />}
              {showChildren && (<ErrorBoundary>{children}</ErrorBoundary>)}
            </div>
          </SidebarInset>
        </div>
        {/* duration matches the old MUI Snackbar's autoHideDuration */}
        <Toaster position="top-right" duration={5000} />
        {(!isLoading && !error && !shouldRedirect) && <CookieConsent />}
      </SidebarProvider>
    </React.Fragment>
  );
};

export default PageLayout;
